import React, { useEffect, useCallback } from "react";
import { Button, Col, Row } from "react-bootstrap";
import styled from "styled-components";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PaidIcon from "@mui/icons-material/Paid";
import {
  ServiciosCompatibles,
  ServicioTransporte,
  useBuscarServiciosCompatibles,
} from "../../../../services/transporteServicios";
import { HeaderSection } from "../HeaderSection";
import { Title } from "../Title";
import ServerErrorModal from "../../../organisms/ServerErrorModal";
import { Model } from "../Model";
import Resumen from "../Resumen/Resumen";
import { useWizard } from "../../../../hooks/wizard";
import DatosTransporte from "../DatosTransporte/DatosTransporte";
import SuccessButton from "../../../organisms/SuccessButton";
import { usePasoReservaHotel } from "../pasoReservaHotel";
import SeccionTarifas from "../../../calculoPrecioTransporte/SeccionTarifas";

function CalculoPrecioTransporte({ model }: { model: Model }) {
  const {
    currentModel,
    definirTarifaIda,
    definirTarifaVuelta,
    definirTarifaIdaVuelta,
    reiniciarTarifasTransporte,
    definirUsarTarifasPorTrayecto,
  } = usePasoReservaHotel(model);
  const { avanza, vuelve, volver, avanzar } = useWizard();

  const {
    data: planesCompatibles,
    isLoading: planesCompatiblesIsLoading,
    error: planesCompatiblesError,
  } = useBuscarServiciosCompatibles(model.sectorTransporte!!.id);

  const autoSeleccionarTarifasSingulares = useCallback(
    (serviciosCompatibles: ServiciosCompatibles, _model: Model) => {
    if (mostrarTarifasIda(_model)) {
      const tarifasIda = serviciosCompatibles.unTrayecto;
      if (tarifasIda.length === 1) {
        definirTarifaIda({ tarifaIda: tarifasIda[0] });
      }
    }
    if (mostrarTarifasVuelta(_model)) {
      const tarifasVuelta = serviciosCompatibles.unTrayecto;
      if (tarifasVuelta.length === 1) {
        definirTarifaVuelta({ tarifaVuelta: tarifasVuelta[0] });
      }
    }
    if (mostrarTarifasIdaVuelta(_model)) {
      const tarifasIdaVuelta = serviciosCompatibles.dosTrayectos;
      if (tarifasIdaVuelta.length === 1) {
        definirTarifaIdaVuelta({ tarifaIdaVuelta: tarifasIdaVuelta[0] });
      }
    }
  }, [definirTarifaIda, definirTarifaVuelta, definirTarifaIdaVuelta]);

  const onSeleccionarTarifaIda = (tarifa: ServicioTransporte) => {
    definirTarifaIda({ tarifaIda: tarifa });
  };

  const onSeleccionarTarifaVuelta = (tarifa: ServicioTransporte) => {
    definirTarifaVuelta({ tarifaVuelta: tarifa });
  };

  const onSeleccionarTarifaIdaYVuelta = (tarifa: ServicioTransporte) => {
    definirTarifaIdaVuelta({ tarifaIdaVuelta: tarifa });
  };

  const onUsarTarifasPorTrayectoChange = () => {
    definirUsarTarifasPorTrayecto({
      usarTarifasPorTrayecto: !currentModel.usarTarifasPorTrayecto,
    });
    reiniciarTarifasTransporte();
    if (planesCompatibles) {
      autoSeleccionarTarifasSingulares(planesCompatibles, currentModel);
    }
  };

  useEffect(() => {
    if (
      planesCompatibles &&
      !currentModel.tarifaIda &&
      !currentModel.tarifaVuelta &&
      !currentModel.tarifaIdaVuelta
    ) {
      autoSeleccionarTarifasSingulares(planesCompatibles, currentModel);
    }
  }, [currentModel, planesCompatibles, autoSeleccionarTarifasSingulares]);

  const puedeAvanzar = calcularPuedeAvanzar(currentModel);

  const error = planesCompatiblesError;

  if (avanza) {
    return <Resumen model={currentModel} />;
  }

  if (vuelve) {
    return <DatosTransporte model={currentModel} />;
  }

  return (
    <>
      {error && <ServerErrorModal />}
      <Row className="justify-content-center">
        <Col className="col-10">
          <HeaderSection className="pt-2 pb-2">
            <Title>
              Cálculo de precios de transporte{""}
              <IconContainer>
                <PaidIcon sx={{ fontSize: 26 }} />
              </IconContainer>
            </Title>
          </HeaderSection>
          <Row>
            <div>
              <b>Sector: </b> {model.sectorTransporte!!.nombre}
            </div>
            {model.contrataIda && model.contrataVuelta && (
              <div className="mb-4 mt-3">
                <label className="mb-1">Usar tarifas por cada trayecto</label>
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="flexSwitchCheckDefault"
                    checked={!!currentModel.usarTarifasPorTrayecto}
                    onChange={onUsarTarifasPorTrayectoChange}
                  />
                  <label>
                    {!!currentModel.usarTarifasPorTrayecto ? "Si" : "No"}{" "}
                  </label>
                </div>
              </div>
            )}
            {mostrarTarifasIda(currentModel) && (
              <SeccionTarifas
                tipoTrayecto="ida"
                isLoading={planesCompatiblesIsLoading}
                tarifas={planesCompatibles?.unTrayecto || []}
                onSeleccionarTarifa={onSeleccionarTarifaIda}
                tarifaSeleccionada={currentModel.tarifaIda}
              />
            )}
            {mostrarTarifasVuelta(currentModel) && (
              <SeccionTarifas
                tipoTrayecto="vuelta"
                isLoading={planesCompatiblesIsLoading}
                tarifas={planesCompatibles?.unTrayecto || []}
                onSeleccionarTarifa={onSeleccionarTarifaVuelta}
                tarifaSeleccionada={currentModel.tarifaVuelta}
              />
            )}

            {mostrarTarifasIdaVuelta(currentModel) && (
              <SeccionTarifas
                tipoTrayecto="ida_y_vuelta"
                isLoading={planesCompatiblesIsLoading}
                tarifas={planesCompatibles?.dosTrayectos || []}
                onSeleccionarTarifa={onSeleccionarTarifaIdaYVuelta}
                tarifaSeleccionada={currentModel.tarifaIdaVuelta}
              />
            )}
            <div className="d-flex mb-2 justify-content-between">
              <Button
                type={"button"}
                style={{ width: "fit-content" }}
                onClick={volver}
              >
                <ArrowBackIcon sx={{ fontSize: 20 }} />
                Volver
              </Button>
              <SuccessButton
                type={"button"}
                style={{ width: "fit-content" }}
                disabled={!puedeAvanzar}
                onClick={avanzar}
              >
                Siguiente <ArrowForwardIcon sx={{ fontSize: 20 }} />
              </SuccessButton>
            </div>
          </Row>
        </Col>
      </Row>
    </>
  );
}

const IconContainer = styled("span")`
  display: relative;
  color: ${(props) => props.theme.colors.main};
  margin-left: 4px;
`;

const calcularPuedeAvanzar = (model: Model): boolean => {
  if (!!model.tarifaIdaVuelta) {
    return true;
  }

  if (model.contrataIda && !model.tarifaIda) {
    return false;
  }

  if (model.contrataVuelta && !model.tarifaVuelta) {
    return false;
  }

  return true;
};

const mostrarTarifasIda = (model: Model) => {
  return (
    (model.contrataIda &&
      model.contrataVuelta &&
      model.usarTarifasPorTrayecto) ||
    (model.contrataIda && !model.contrataVuelta)
  );
};

const mostrarTarifasVuelta = (model: Model) => {
  return (
    (model.contrataVuelta &&
      model.contrataIda &&
      model.usarTarifasPorTrayecto) ||
    (model.contrataVuelta && !model.contrataIda)
  );
};

const mostrarTarifasIdaVuelta = (model: Model) => {
  return (
    model.contrataIda && model.contrataVuelta && !model.usarTarifasPorTrayecto
  );
};

export default CalculoPrecioTransporte;
